import * as THREE from "three";
import { ResourceID } from "../../../../resource_loader/resource_id.js";
import { InstanceBatch } from "../../../core/batches/instance_batch.js";
import { Vfx3DGlobalAtlas } from "../../../core/textures/vfx_3d_global_atlas.js";
import { VfxGradientAtlas } from "../../../core/textures/vfx_gradient_atlas.js";
import { AttributeSize } from "../../../core/types/core_types.js";

export const FireworkTrailLayout = {
  aPosStartTime: AttributeSize.Vec4, // x, y, z, start_time
  aVelDur:       AttributeSize.Vec4, // vx, vy, vz, duration
  aSizeFade:     AttributeSize.Vec4, // size.x, size.y, fade_in_end, fade_out_start
  aGradParams:   AttributeSize.Vec4, // gradient_row, max_alpha, unused, unused
  aUvBounds:     AttributeSize.Vec4, // min_x, min_y, max_x, max_y
} as const;

export abstract class VfxFireworkTrailBatchBase extends InstanceBatch<typeof FireworkTrailLayout>
{
  public constructor( blending: THREE.Blending, max_instances: number, render_order: number )
  {
    const base_geometry = new THREE.PlaneGeometry( 1, 1 );

    let atlas_texture: THREE.Texture | null = null;
    let gradient_texture: THREE.Texture | null = null;
    try
    {
      atlas_texture = Vfx3DGlobalAtlas.getTexture();
    }
    catch ( _e ) {}
    try
    {
      gradient_texture = VfxGradientAtlas.getTexture();
    }
    catch ( _e ) {}

    const material = new THREE.ShaderMaterial( {
      uniforms: {
        uTime:            { value: 0.0 },
        uTexture:         { value: atlas_texture },
        uGradientTexture: { value: gradient_texture },
      },
      vertexShader: `
        uniform float uTime;

        attribute vec4 aPosStartTime;
        attribute vec4 aVelDur;
        attribute vec4 aSizeFade;
        attribute vec4 aGradParams;
        attribute vec4 aUvBounds;

        varying vec2 vUv;
        varying float vProgress;
        varying float vGradRow;
        varying float vAlpha;

        void main() {
          vUv = mix( aUvBounds.xy, aUvBounds.zw, uv );
          
          float start_time = aPosStartTime.w;
          float duration = aVelDur.w;
          float age = max( 0.0, uTime - start_time );
          float progress = clamp( age / duration, 0.0, 1.0 );
          vProgress = progress;
          vGradRow = aGradParams.x;

          // Branchless Alpha Fade (Precalculated per-vertex for quads)
          float fi = max( 0.0001, aSizeFade.z );
          float fo = clamp( aSizeFade.w, 0.0, 0.9999 );
          float alphaIn = clamp( progress / fi, 0.0, 1.0 );
          float alphaOut = 1.0 - clamp( ( progress - fo ) / ( 1.0 - fo ), 0.0, 1.0 );
          vAlpha = min( alphaIn, alphaOut ) * aGradParams.y;
          
          vec3 computed_pos = aPosStartTime.xyz + aVelDur.xyz * age;
          vec4 mvPosition = modelViewMatrix * vec4( computed_pos, 1.0 );
          mvPosition.xyz += position * vec3( aSizeFade.xy, 1.0 );
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform sampler2D uTexture;
        uniform sampler2D uGradientTexture;

        varying vec2 vUv;
        varying float vProgress;
        varying float vGradRow;
        varying float vAlpha;

        void main() {
          vec4 texColor = texture2D( uTexture, vUv );
          vec4 gradColor = texture2D( uGradientTexture, vec2( vProgress, vGradRow ) );

          gl_FragColor = vec4( gradColor.rgb, texColor.a * vAlpha );
        }
      `,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: blending,
      side: THREE.FrontSide,
    } );

    super( { max_instances, layout: FireworkTrailLayout }, base_geometry, material );
    this.instanced_mesh.renderOrder = render_order;
  }

  public spawnParticle(
    res_id: ResourceID,
    gradient_row: number,
    start_pos: THREE.Vector3,
    velocity: THREE.Vector3,
    size: THREE.Vector2,
    start_time: number,
    duration: number,
    fade_in_end: number,
    fade_out_start: number,
    max_alpha: number = 1.0
  ): number
  {
    const mat = this.material as THREE.ShaderMaterial;
    if ( !mat.uniforms.uTexture.value )
    {
      try
      {
        mat.uniforms.uTexture.value = Vfx3DGlobalAtlas.getTexture();
      }
      catch ( _e ) {}
    }
    if ( !mat.uniforms.uGradientTexture.value )
    {
      try
      {
        mat.uniforms.uGradientTexture.value = VfxGradientAtlas.getTexture();
      }
      catch ( _e ) {}
    }

    const idx = this.allocateInstanceIndex( start_time, duration );
    if ( idx === -1 )
      return -1;

    const bounds = Vfx3DGlobalAtlas.getUVRect( res_id );

    const pos_start_attr  = this.geometry.getAttribute( "aPosStartTime" ).array as Float32Array;
    const vel_dur_attr    = this.geometry.getAttribute( "aVelDur" ).array as Float32Array;
    const size_fade_attr  = this.geometry.getAttribute( "aSizeFade" ).array as Float32Array;
    const grad_alpha_attr = this.geometry.getAttribute( "aGradParams" ).array as Float32Array;
    const uv_bounds_attr  = this.geometry.getAttribute( "aUvBounds" ).array as Float32Array;

    const o = idx * 4;

    pos_start_attr[o]     = start_pos.x;
    pos_start_attr[o + 1] = start_pos.y;
    pos_start_attr[o + 2] = start_pos.z;
    pos_start_attr[o + 3] = start_time;

    vel_dur_attr[o]     = velocity.x;
    vel_dur_attr[o + 1] = velocity.y;
    vel_dur_attr[o + 2] = velocity.z;
    vel_dur_attr[o + 3] = duration;

    size_fade_attr[o]     = size.x;
    size_fade_attr[o + 1] = size.y;
    size_fade_attr[o + 2] = fade_in_end;
    size_fade_attr[o + 3] = fade_out_start;

    grad_alpha_attr[o]     = gradient_row;
    grad_alpha_attr[o + 1] = max_alpha;
    grad_alpha_attr[o + 2] = 0.0;
    grad_alpha_attr[o + 3] = 0.0;

    uv_bounds_attr[o]     = bounds.min_x;
    uv_bounds_attr[o + 1] = bounds.min_y;
    uv_bounds_attr[o + 2] = bounds.max_x;
    uv_bounds_attr[o + 3] = bounds.max_y;

    this.geometry.getAttribute( "aPosStartTime" ).needsUpdate = true;
    this.geometry.getAttribute( "aVelDur" ).needsUpdate       = true;
    this.geometry.getAttribute( "aSizeFade" ).needsUpdate     = true;
    this.geometry.getAttribute( "aGradParams" ).needsUpdate   = true;
    this.geometry.getAttribute( "aUvBounds" ).needsUpdate     = true;

    return this.getStableId( idx );
  }

  protected override onUpdate( _dt: number, now: number ): void
  {
    const mat = this.material as THREE.ShaderMaterial;
    if ( mat.uniforms.uTime )
    {
      mat.uniforms.uTime.value = now;
    }
  }

  protected override updateInstance( _progress: number, _now: number, _idx: number ): boolean
  {
    return true;
  }
}

export class VfxFireworkTrailAdditiveBatch extends VfxFireworkTrailBatchBase
{
  private static readonly MAX_INSTANCES: number = 907;
  private static readonly RENDER_ORDER: number  = 29;

  public constructor()
  {
    super( THREE.AdditiveBlending, VfxFireworkTrailAdditiveBatch.MAX_INSTANCES, VfxFireworkTrailAdditiveBatch.RENDER_ORDER );
  }
}

export class VfxFireworkTrailNormalBatch extends VfxFireworkTrailBatchBase
{
  private static readonly MAX_INSTANCES: number = 2570;
  private static readonly RENDER_ORDER: number  = 30;

  public constructor()
  {
    super( THREE.NormalBlending, VfxFireworkTrailNormalBatch.MAX_INSTANCES, VfxFireworkTrailNormalBatch.RENDER_ORDER );
  }
}

export { VfxFireworkTrailAdditiveBatch as VfxFireworkTrailBatch, VfxFireworkTrailNormalBatch as VfxFireworkTrailSmokeBatch };



